import {
    memo,
    type PropsWithChildren,
    type SyntheticEvent
} from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { getFormInputProps } from "util/forms";
import { getOptionName } from "util/option";
import JsonEditor from "lib/jsoneditor";
import { selectBanksFlowsOptions } from "features/general/selectors";
import Tabs from "ui/atoms/Tabs";
import { AssetTextField } from "ui/atoms/TextField";
import When from "ui/support/When";
import Loader from "ui/molecules/Loader";
import { CountryIso2 } from "ui/widgets/CountrySelector";
import StatefulSelector from "ui/widgets/StatefulSelector";
import { Checkbox } from "ui/forms/formik-elements";
import { FormField, type IbanXSBanksCountriesPivotProps } from "./types";
import { ToggleEnabledOpenBankingResource } from "../shared";

const IbanXSBanksCountriesPivot = ({
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    isBootstrapDataLoading,
    bootstrapData,
    children,
    initialValues,
    ...restProps
}: PropsWithChildren<IbanXSBanksCountriesPivotProps>) => {
    const onJsonEditorChange = (formField: string) => (value: string | object) => {
        setFieldValue(formField, value, true);
        setTimeout(() => setFieldTouched(formField, true));
    };

    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    return (
        <When
            if={Boolean(values[FormField.BankId])}
            then={(
                <Box
                    component='form'
                    onSubmit={handleSubmit}
                    autoComplete='off'
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: {
                                xs: 2,
                                sm: 3
                            },
                            alignItems: 'flex-start'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    lg: 'repeat(2, 1fr)'
                                },
                                gap: 2
                            }}
                        >
                            <TextField
                                fullWidth
                                required
                                disabled
                                size='small'
                                name={FormField.BankId}
                                value={initialValues[FormField.BankId]}
                                label='Bank ID'
                            />
                            <TextField
                                {...getFormInputProps(FormField.FullName, restProps)}
                                fullWidth
                                required
                                size='small'
                                name={FormField.FullName}
                                value={values[FormField.FullName]}
                                label='Full Name'
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <CountryIso2
                                fullWidth
                                disableClearable
                                value={values[FormField.Iso2]}
                                onChange={onAutocompleteChange(FormField.Iso2)}
                                TextFieldProps={{
                                    ...getFormInputProps(FormField.Iso2, restProps),
                                    required: true,
                                    name: FormField.Iso2,
                                    label: 'Countries Available'
                                }}
                            />
                            <AssetTextField
                                {...getFormInputProps(FormField.LogoUrl, restProps)}
                                fullWidth
                                inputProps={{
                                    maxLength: 191
                                }}
                                size='small'
                                name={FormField.LogoUrl}
                                value={values[FormField.LogoUrl] || ''}
                                label={`Logo URL (${bootstrapData.OpenBankingLogosDomain})`}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <TextField
                                {...getFormInputProps(FormField.BankGroup, restProps)}
                                fullWidth
                                size='small'
                                name={FormField.BankGroup}
                                value={values[FormField.BankGroup] || ''}
                                label='Bank Group'
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <StatefulSelector
                                fullWidth
                                size='small'
                                layout={null}
                                loading={isBootstrapDataLoading}
                                disabled={isBootstrapDataLoading}
                                selectOptions={selectBanksFlowsOptions}
                                value={values[FormField.Flow]}
                                getOptionLabel={getOptionName}
                                onChange={onAutocompleteChange(FormField.Flow)}
                                TextFieldProps={{
                                    ...getFormInputProps(FormField.Flow, restProps),
                                    name: FormField.Flow,
                                    label: 'Flow'
                                }}
                            />
                            <TextField
                                {...getFormInputProps(FormField.Obs, restProps)}
                                fullWidth
                                multiline
                                rows={4}
                                size='small'
                                name={FormField.Obs}
                                value={values[FormField.Obs]}
                                label='Description'
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <Box
                                sx={{
                                    display: 'grid'
                                }}
                            >
                                <ToggleEnabledOpenBankingResource
                                    initialValues={initialValues}
                                    setFieldValue={setFieldValue}
                                    setFieldTouched={setFieldTouched}
                                    handleBlur={handleBlur}
                                    CheckboxProps={{
                                        name: FormField.IsEnabled,
                                        checked: values[FormField.IsEnabled]
                                    }}
                                />
                                <Checkbox
                                    setFieldValue={setFieldValue}
                                    setFieldTouched={setFieldTouched}
                                    label='Should Credit On Redirect'
                                    handleBlur={handleBlur}
                                    CheckboxProps={{
                                        inputProps: {
                                            'aria-label': 'Should Credit On Redirect'
                                        },
                                        name: FormField.ShouldCreditOnRedirect,
                                        checked: values[FormField.ShouldCreditOnRedirect]
                                    }}
                                />
                            </Box>
                        </Box>
                        <Tabs
                            model={[
                                {
                                    label: 'Options',
                                    component: (
                                        <JsonEditor
                                            value={values[FormField.Options]}
                                            onChange={onJsonEditorChange(FormField.Options)}
                                        />
                                    )
                                }
                            ]}
                            TabListProps={{
                                'aria-label': 'iban xs banks countries pivot tabs',
                                variant: 'scrollable',
                                scrollButtons: 'auto'
                            }}
                        />
                    </Box>
                    {children}
                </Box>
            )}
        >
            <Loader
                isLoading
                sx={{
                    height: 'calc(100vh - 200px)',
                    display: 'grid',
                    placeContent: 'center'
                }}
            />
        </When>
    );
};

export default memo(IbanXSBanksCountriesPivot);
